import dayjs from "dayjs";
import PropTypes from "prop-types";

import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

import { formatDate, formatDateTime } from "../../features/shared";

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  header: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
    marginTop: 16,
    marginBottom: 12,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#ececec",
    fontFamily: "Helvetica-Bold",
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tableRow: {
    flexDirection: "row",
    borderColor: "#ececec",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  cell: {
    padding: 4,
  },
  rightAlign: {
    textAlign: "right",
  },
  attributeHeader: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    marginTop: 8,
  },
});

export const SalesOrderProductVariantRetailPdf = ({
  data,
  user,
  outlet,
  dateRange,
}) => {
  const [startDate, endDate] = dateRange;
  const {
    generalProductVariants,
    readyLensProductVariants,
    preOrderLensProducts,
  } = data;
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: 16,
          }}
        >
          <Text style={styles.header}>Penjualan Per Produk Varian</Text>
          <Text
            style={{
              fontSize: 12,
              color: "#364F6B",
              fontFamily: "Helvetica-Bold",
            }}
          >
            {outlet.name}
          </Text>
        </View>

        {/* Information Section */}
        <View style={{ flexDirection: "row", marginVertical: 16 }}>
          <View style={{ flex: 1 }}>
            <Text>Tanggal Download</Text>
            <Text style={styles.attributeHeader}>
              {formatDateTime(dayjs())}
            </Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text>Didownload Oleh</Text>
            <Text style={styles.attributeHeader}>{user.name}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text>Dari Tanggal - Sampai Tanggal</Text>
            <Text style={styles.attributeHeader}>
              {`${formatDate(startDate)} - ${formatDate(endDate)}`}
            </Text>
          </View>
        </View>

        {/* General Products Table */}
        <Text style={styles.sectionTitle}>Produk Umum</Text>
        <View style={styles.tableHeader}>
          <Text style={[styles.cell, { flex: 3 }]}>SKU</Text>
          <Text style={[styles.cell, { flex: 3 }]}>Nama Barang</Text>
          <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
            Qty
          </Text>
        </View>
        {generalProductVariants.map((productVariant) => (
          <View style={styles.tableRow} key={productVariant.sku}>
            <Text style={[styles.cell, { flex: 3 }]}>{productVariant.sku}</Text>
            <Text style={[styles.cell, { flex: 3 }]}>
              {productVariant.name}
            </Text>
            <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
              {productVariant.quantity}
            </Text>
          </View>
        ))}

        {/* Ready Lens Products Table */}
        <Text style={styles.sectionTitle}>Produk Lensa (Ready)</Text>
        <View style={styles.tableHeader}>
          <Text style={[styles.cell, { flex: 8 }]}>SKU</Text>
          <Text style={[styles.cell, { flex: 8 }]}>Nama Barang</Text>
          <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
            Sph
          </Text>
          <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
            Cyl
          </Text>
          <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
            Add
          </Text>
          <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
            Qty
          </Text>
        </View>
        {readyLensProductVariants.map((productVariant) => (
          <View style={styles.tableRow} key={productVariant.sku}>
            <Text style={[styles.cell, { flex: 8 }]}>{productVariant.sku}</Text>
            <Text style={[styles.cell, { flex: 8 }]}>
              {productVariant.name}
            </Text>
            <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
              {productVariant.variantAttribute1Value ?? "-"}
            </Text>
            <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
              {productVariant.variantAttribute2Value ?? "-"}
            </Text>
            <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
              {productVariant.variantAttribute3Value ?? "-"}
            </Text>
            <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
              {productVariant.quantity}
            </Text>
          </View>
        ))}

        {/* Pre-Order Lens Products Table */}
        <Text style={styles.sectionTitle}>Produk Lensa (Pre-Order)</Text>
        {preOrderLensProducts.map((lensProduct, index) => (
          <View
            key={lensProduct.id}
            wrap={false}
            style={{ marginTop: index === 0 ? 0 : 12 }}
          >
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text>{`${index + 1}. ${lensProduct.name}`}</Text>
              <Text
                style={{
                  fontFamily: "Helvetica-Oblique",
                  color: "#757575",
                }}
              >
                {lensProduct.referenceNumber} ({lensProduct.buyer.name})
              </Text>
            </View>

            <View style={[styles.tableHeader, { marginTop: 8 }]}>
              <Text style={[styles.cell, { flex: 1 }]}>Sisi</Text>
              <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
                Sph
              </Text>
              <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
                Cyl
              </Text>
              <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
                Add
              </Text>
              <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
                Axis
              </Text>
              <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
                PD
              </Text>
              <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
                Prism
              </Text>
            </View>
            {lensProduct.lensProductVariants.map((variant) => {
              let note;
              try {
                note = JSON.parse(variant.note || "{}");
              } catch {
                // fallback if JSON is invalid
                note = {};
              }

              return (
                <View key={variant.side} style={styles.tableRow}>
                  <Text style={[styles.cell, { flex: 1 }]}>{variant.side}</Text>
                  {/* Sph */}
                  <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
                    {variant.variantAttribute1Value ?? "-"}
                  </Text>
                  {/* Cyl */}
                  <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
                    {variant.variantAttribute2Value ?? "-"}
                  </Text>
                  {/* Add */}
                  <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
                    {variant.variantAttribute3Value ?? "-"}
                  </Text>
                  <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
                    {note.axis ?? "-"}
                  </Text>
                  <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
                    {note.pd ?? "-"}
                  </Text>
                  <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
                    {note.prism ?? "-"}
                  </Text>
                </View>
              );
            })}

            {/* Display Add-ons if available */}
            {lensProduct.addOns && lensProduct.addOns.length > 0 && (
              <Text style={{ marginTop: 4, fontSize: 10 }}>
                Add-on:{" "}
                <Text style={{ color: "red", fontFamily: "Helvetica-Bold" }}>
                  {lensProduct.addOns.join(", ")}
                </Text>
              </Text>
            )}

            {/* Display Notes if available */}
            {lensProduct.note && (
              <Text
                style={{
                  marginTop: 4,
                  fontSize: 10,
                  fontFamily: "Helvetica-Oblique",
                  color: "#757575",
                }}
              >
                Note: {lensProduct.note}
              </Text>
            )}
          </View>
        ))}
      </Page>
    </Document>
  );
};

SalesOrderProductVariantRetailPdf.propTypes = {
  data: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  outlet: PropTypes.object.isRequired,
  dateRange: PropTypes.array.isRequired,
};
